import { useState, useEffect, useRef } from 'react';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@rocco/components/popover';
import { FormItem, FormLabel, FormMessage } from '@rocco/components/form';
import type { BuildHubOption } from '@ui-rocco/hooks/use-build-hubs';
import { cn } from '@rocco/utils/cn';
import { AvatarCircles } from '@rocco/components/avatar-circles';
import { getCollaboratorAvatar } from '@modules/build-hub/utils/get-collaborator-avatar';
import { HousePlus, Check, ChevronDown, Pencil } from 'lucide-react';
import { useRootData } from '@modules/root';
import { getGoogleStaticMapForBuildHub } from '@modules/build-hub/utils/get-google-static-map';
import { BuildHubInlinePreview } from './BuildHubInlinePreview';
import type { BuildHubFieldSelection } from '../../hooks/use-build-hub-field-selection';
import { InlineCreateBuildHubForm } from './InlineCreateBuildHubForm';
import { BUILD_HUB_STAGE_CONFIG_API_ROUTE } from '~/routes/remix-api.build-hub.stage-config';
import type { StageConfiguration } from '@modules/build-hub/configs/stage-config';
import { useUserLoggedIn } from '@modules/user';

/** Variant type for different styling contexts. */
type BuildHubSelectorVariant = 'enquiry' | 'hde';

interface BuildHubSelectorBaseProps {
    /** The currently selected BuildHub ID. */
    value?: string;
    /** Callback when a BuildHub is selected. */
    onChange: (value: string | undefined) => void;
    /** Whether the selector is disabled. */
    disabled?: boolean;
    /** Additional className for the container. */
    className?: string;
    /** Callback to save form state when selection changes. */
    onBlur?: () => void;
    /** List of available BuildHub projects. */
    buildHubs?: BuildHubOption[];
    /** Whether the list is loading. */
    isLoading: boolean;
    /** Callback to refetch the buildHubs list. */
    onRefetchBuildHubs?: () => void;
    /** Variant for styling - 'enquiry' (default) or 'hde'. */
    variant?: BuildHubSelectorVariant;
    /** Label text for the selector (used in HDE variant). */
    label?: string;
}

/** Props specific to the enquiry variant. */
interface EnquiryVariantProps extends BuildHubSelectorBaseProps {
    variant?: 'enquiry';
    /** Current field selection state (required for enquiry variant). */
    fieldSelection: BuildHubFieldSelection;
    /** Callback when field selection changes (required for enquiry variant). */
    onFieldSelectionChange: (selection: BuildHubFieldSelection) => void;
    /** Callback when user chooses manual entry (not used in enquiry). */
    onManualEntry?: never;
}

/** Props specific to the HDE variant. */
interface HDEVariantProps extends BuildHubSelectorBaseProps {
    variant: 'hde';
    /** Callback when user chooses to enter details manually. */
    onManualEntry: () => void;
    /** Field selection not used in HDE variant. */
    fieldSelection?: never;
    /** Field selection change not used in HDE variant. */
    onFieldSelectionChange?: never;
}

type BuildHubSelectorProps = EnquiryVariantProps | HDEVariantProps;

/**
 * A popover-based selector for choosing a BuildHub project.
 * Supports two variants:
 * - 'enquiry': For enquiry forms with inline creation and field selection preview
 * - 'hde': For HDE registration form with manual entry option and GenericSelect styling
 */
export function BuildHubSelector(props: BuildHubSelectorProps) {
    const {
        value,
        onChange,
        disabled = false,
        className,
        onBlur,
        buildHubs,
        isLoading,
        onRefetchBuildHubs,
        variant = 'enquiry',
        label = 'Select a project',
    } = props;

    const { isLoggedIn } = useUserLoggedIn();
    const [isOpen, setIsOpen] = useState(false);
    const [view, setView] = useState<'list' | 'create'>('list');
    const [stageConfig, setStageConfig] = useState<StageConfiguration | null>(
        null,
    );
    const [stageConfigError, setStageConfigError] = useState<string | null>(
        null,
    );
    const [pendingBuildHubId, setPendingBuildHubId] = useState<string | null>(
        null,
    );
    const { config } = useRootData();

    // Store onChange in a ref to avoid including it in dependency arrays
    const onChangeRef = useRef(onChange);
    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    // Preload stageConfig for inline create feature (both variants)
    useEffect(() => {
        fetch(BUILD_HUB_STAGE_CONFIG_API_ROUTE)
            .then((res) => {
                if (!res.ok) {
                    throw new Error('Failed to load configuration');
                }
                return res.json();
            })
            .then((data) => {
                setStageConfig(data);
            })
            .catch((err) => {
                setStageConfigError(
                    err instanceof Error
                        ? err.message
                        : 'Failed to load configuration',
                );
            });
    }, []);

    // Reset to list view when popover closes while in create mode
    useEffect(() => {
        if (!isOpen && view === 'create') {
            setView('list');
        }
    }, [isOpen, view]);

    // Handle BuildHub creation success (both variants)
    const handleBuildHubCreated = (buildHubId: string) => {
        onChange(buildHubId);
        setView('list');
        setPendingBuildHubId(buildHubId);
        onRefetchBuildHubs?.();

        // For HDE variant, close popover immediately after creation
        if (variant === 'hde') {
            setIsOpen(false);
        }
    };

    // Find the selected project
    const selectedProject = value
        ? buildHubs?.find((p) => String(p.ID) === String(value))
        : undefined;

    // Close popover once the newly created BuildHub appears in the list (both variants)
    useEffect(() => {
        if (pendingBuildHubId && !isLoading && buildHubs) {
            if (!value || String(value) !== String(pendingBuildHubId)) {
                onChangeRef.current(pendingBuildHubId);
                return;
            }

            const foundBuildHub = buildHubs.find(
                (p) => String(p.ID) === String(pendingBuildHubId),
            );

            if (foundBuildHub && selectedProject) {
                const timeout = setTimeout(() => {
                    setPendingBuildHubId(null);
                    // Only close popover if it's not already closed (for enquiry variant)
                    if (variant !== 'hde' || isOpen) {
                        setIsOpen(false);
                    }
                }, 200);

                return () => clearTimeout(timeout);
            }
        }
    }, [
        pendingBuildHubId,
        buildHubs,
        isLoading,
        value,
        selectedProject,
        variant,
        isOpen,
    ]);

    // Handle back to list - reset view state
    const handleBackToList = () => {
        setView('list');
    };

    // Handle project selection
    const handleProjectSelect = (projectId: string) => {
        if (value === projectId) {
            onChange(undefined);
        } else {
            onChange(projectId);
        }
        if (variant === 'hde') {
            setIsOpen(false);
        }
        onBlur?.();
    };

    // Handle manual entry (HDE variant)
    const handleManualEntry = () => {
        if (props.variant === 'hde' && props.onManualEntry) {
            props.onManualEntry();
            setIsOpen(false);
        }
    };

    if (!isLoggedIn) return null;

    // Determine display text
    const displayText = selectedProject
        ? selectedProject.Title
        : variant === 'enquiry'
          ? 'Select or Add a Project'
          : label;

    // Render trigger button based on variant
    const renderTrigger = () => {
        if (variant === 'hde') {
            return (
                <button
                    type="button"
                    disabled={disabled || isLoading}
                    className={cn(
                        // Base styles - matching GenericSelect trigger
                        'font-["neue-haas-grotesk-display",sans-serif] font-semibold',
                        // Size and spacing
                        'flex h-auto w-full items-center justify-between border-0 bg-transparent px-0 outline-hidden',
                        'text-left text-[1.4375rem] leading-[113%] md:text-[1.4375rem]',
                        // Padding
                        'py-5.25 pr-[2.25rem]',
                        // Text color based on value
                        selectedProject ? 'text-black' : 'text-[#b3b3b3]',
                        // Transition
                        'opacity-100 transition-opacity duration-300',
                        // Desktop styles
                        'md:max-w-[37.5rem] md:pr-0',
                        // Focus styles
                        'focus:ring-0',
                        // Disabled styles
                        'disabled:cursor-not-allowed disabled:opacity-50',
                    )}
                >
                    <span className="truncate">{displayText}</span>
                    <ChevronDown
                        className={cn(
                            'size-7 shrink-0 rounded-full border border-black/70 p-1 text-black opacity-100 transition-transform',
                            isOpen && 'rotate-180',
                        )}
                    />
                </button>
            );
        }

        // Enquiry variant trigger
        return (
            <button
                type="button"
                disabled={disabled || isLoading}
                className="flex h-12 w-full items-center justify-between rounded-md border border-gray-200 bg-white px-4 py-2 text-left text-base transition-colors hover:bg-gray-50 focus:ring-2 focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
            >
                <span
                    className={
                        selectedProject ? 'text-gray-900' : 'text-gray-500'
                    }
                >
                    {displayText}
                </span>
                <ChevronDown
                    className={cn(
                        'h-5 w-5 text-gray-400 transition-transform',
                        isOpen && 'rotate-180',
                    )}
                />
            </button>
        );
    };

    // Render action buttons (create new / manual entry)
    const renderActionButtons = () => {
        if (variant === 'hde') {
            return (
                <div className="flex flex-col border-b border-gray-200">
                    {/* Enter details manually option */}
                    <button
                        type="button"
                        onClick={handleManualEntry}
                        className="flex items-center gap-2 px-4 py-3 text-left transition-colors hover:bg-gray-100"
                    >
                        <Pencil className="h-5 w-5 text-gray-700" />
                        <span className="text-base font-medium text-gray-700">
                            Enter details manually
                        </span>
                    </button>
                </div>
            );
        }

        // Enquiry variant - Add a New Project
        return (
            <button
                type="button"
                onClick={() => setView('create')}
                className="mx-2 flex w-fit items-center gap-2 rounded-md px-2 py-2 text-left transition-colors hover:bg-gray-100"
            >
                <span className="text-base font-medium text-gray-700">
                    Add a New Project
                </span>
                <Pencil className="h-4.5 w-4.5 text-gray-700" />
            </button>
        );
    };

    // Render project list item
    const renderProjectItem = (project: BuildHubOption) => {
        const isSelected = value === project.ID;

        const mapFallbackUrl =
            !project.Thumbnail && project.Location
                ? getGoogleStaticMapForBuildHub(
                      project.Location,
                      config.pixels.websiteGoogleMapKey,
                      {
                          width: 80,
                          height: 80,
                          zoomLevel: 13,
                          showMarker: false,
                      },
                  )
                : null;

        return (
            <button
                key={project.ID}
                type="button"
                onClick={() => handleProjectSelect(project.ID)}
                className={cn(
                    'mb-2 flex w-full items-center gap-3 rounded-md bg-[#f6f6f6] px-4 py-3 text-left transition-colors last:mb-0 hover:bg-gray-100',
                    variant === 'enquiry' &&
                        isSelected &&
                        'shadow-[0_1px_8px_0_rgba(0,0,0,0.25)]',
                    variant === 'hde' && isSelected && 'ring-2 ring-black',
                )}
            >
                {/* Thumbnail with map fallback */}
                <div
                    className={cn(
                        'relative shrink-0 overflow-hidden rounded-md bg-gray-200',
                        variant === 'enquiry'
                            ? 'h-10 w-10 md:h-20 md:w-20'
                            : 'h-12 w-12',
                    )}
                >
                    {project.Thumbnail ? (
                        <img
                            src={project.Thumbnail}
                            alt={project.Title}
                            className="h-full w-full object-cover"
                        />
                    ) : mapFallbackUrl ? (
                        <img
                            src={mapFallbackUrl}
                            alt={`Map of ${project.Location}`}
                            className="h-full w-full object-cover"
                        />
                    ) : (
                        <div className="flex h-full w-full items-center justify-center text-gray-400">
                            <HousePlus className="h-6 w-6" />
                        </div>
                    )}
                    {/* Selected indicator */}
                    {isSelected && (
                        <div
                            className={cn(
                                'absolute inset-0 flex items-center justify-center',
                                variant === 'enquiry'
                                    ? 'bg-black/20'
                                    : 'bg-black/30',
                            )}
                        >
                            <div
                                className={cn(
                                    'flex items-center justify-center rounded-full',
                                    variant === 'enquiry'
                                        ? 'h-6 w-6 bg-[#D36D48] md:h-10 md:w-10'
                                        : 'h-6 w-6 bg-black',
                                )}
                            >
                                <Check
                                    className={cn(
                                        variant === 'enquiry'
                                            ? 'size-4 text-black md:h-6 md:w-6'
                                            : 'h-4 w-4 text-white',
                                    )}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Text Info */}
                <div className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-base font-medium text-gray-900">
                        {project.Title}
                    </span>
                    <span className="truncate text-sm text-gray-500">
                        {project.ProjectScope ||
                            project.ProjectType ||
                            (variant === 'enquiry'
                                ? 'Residential New Build'
                                : 'Residential Project')}
                    </span>
                </div>

                {/* Collaborators (enquiry variant only) */}
                {variant === 'enquiry' &&
                    project.Collaborators &&
                    project.Collaborators.length > 0 && (
                        <div className="ml-2 hidden shrink-0 md:block">
                            <AvatarCircles
                                avatarUrls={project.Collaborators.map((c) => ({
                                    imageUrl: getCollaboratorAvatar(c),
                                    name: c.name || c.email,
                                }))}
                                maxAvatars={3}
                                size="square40"
                            />
                        </div>
                    )}
            </button>
        );
    };

    // Render popover content
    const renderPopoverContent = () => {
        // Show create form for both variants when in create view
        if (view === 'create') {
            return (
                <div className="flex flex-col overflow-y-scroll rounded-md border border-gray-200 bg-white px-2 py-3 shadow-lg md:max-h-[400px]">
                    {stageConfigError && (
                        <div className="p-6 text-center text-red-600">
                            Error loading form: {stageConfigError}
                        </div>
                    )}
                    {stageConfig && (
                        <InlineCreateBuildHubForm
                            stageConfig={stageConfig}
                            onBuildHubCreated={handleBuildHubCreated}
                            onBackToList={handleBackToList}
                            variant={variant}
                        />
                    )}
                    {!stageConfig && !stageConfigError && (
                        <div className="p-6 text-center text-gray-500">
                            Loading form...
                        </div>
                    )}
                </div>
            );
        }

        return (
            <div className="flex max-h-[400px] flex-col overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg">
                {/* Action buttons */}
                {renderActionButtons()}

                {/* Project list */}
                <div
                    className={cn(
                        'max-h-[350px] overflow-y-auto px-3 py-2',
                        variant === 'enquiry' && 'mb-6',
                    )}
                >
                    {buildHubs?.length === 0 && !isLoading && (
                        <div className="py-4 text-center text-gray-500">
                            No projects found. Create one above.
                        </div>
                    )}
                    {isLoading && (
                        <div className="py-4 text-center text-gray-500">
                            Loading projects...
                        </div>
                    )}
                    {buildHubs?.map(renderProjectItem)}
                </div>
            </div>
        );
    };

    // Render the component based on variant
    if (variant === 'hde') {
        return (
            <div className={cn('relative w-full', className)}>
                <div className="relative w-full border-b border-[rgb(47,47,47)]/70">
                    <Popover open={isOpen} onOpenChange={setIsOpen}>
                        <PopoverTrigger asChild>
                            {renderTrigger()}
                        </PopoverTrigger>
                        {/*
                         * Portal to <body>: the enquiry/HDE dialog centres with
                         * `translate-x/y`, which in Tailwind v4 sets the `translate`
                         * property (not `transform`). Floating UI's containing-block
                         * check only looks at `transform`, so a non-portalled popover
                         * mis-anchors to the dialog's bottom-right (BB-3732). Portalling
                         * escapes the translated ancestor and restores master's anchor.
                         */}
                        <PopoverContent
                            className="w-(--radix-popover-trigger-width) p-0 md:max-w-[37.5rem]"
                            align="start"
                            sideOffset={4}
                            usePortal
                        >
                            {renderPopoverContent()}
                        </PopoverContent>
                    </Popover>
                </div>

                {/* Selected project preview (HDE variant) */}
                {selectedProject && (
                    <div className="mt-4 rounded-lg bg-[#f6f6f6] p-4">
                        <div className="flex items-start gap-4">
                            <div className="h-16 w-16 shrink-0 overflow-hidden rounded-md bg-gray-200">
                                {selectedProject.Thumbnail ? (
                                    <img
                                        src={selectedProject.Thumbnail}
                                        alt={selectedProject.Title}
                                        className="h-full w-full object-cover"
                                    />
                                ) : (
                                    <div className="flex h-full w-full items-center justify-center text-gray-400">
                                        <HousePlus className="h-8 w-8" />
                                    </div>
                                )}
                            </div>
                            <div className="flex-1">
                                <h4 className="text-lg font-semibold text-black">
                                    {selectedProject.Title}
                                </h4>
                                {selectedProject.Location && (
                                    <p className="text-sm text-gray-600">
                                        {selectedProject.Location}
                                    </p>
                                )}
                                {selectedProject.ProjectScope && (
                                    <p className="text-sm text-gray-500">
                                        {selectedProject.ProjectScope}
                                    </p>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => onChange(undefined)}
                                className="text-sm text-gray-500 underline hover:text-black"
                            >
                                Change
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // Enquiry variant
    return (
        <FormItem className={cn('md:col-span-2', className)}>
            <FormLabel className="text-base" errorVariant="errorDarker">
                {`Include your Project Details`}
            </FormLabel>
            <Popover open={isOpen} onOpenChange={setIsOpen}>
                <PopoverTrigger asChild>{renderTrigger()}</PopoverTrigger>
                {/* Portal to <body> — see the variant above; v4 `translate`
                 * dialog centring breaks Floating UI anchoring otherwise (BB-3732). */}
                <PopoverContent
                    className="w-(--radix-popover-trigger-width) p-0"
                    align="start"
                    sideOffset={4}
                    usePortal
                >
                    {renderPopoverContent()}
                </PopoverContent>
            </Popover>
            {!selectedProject && (
                <div className="mt-2 text-base text-gray-500">
                    *Including your Project Details is not required, but it
                    increases reply rates and response quality. This is to
                    provide context to your enquiry.
                </div>
            )}
            {selectedProject && props.variant !== 'hde' && (
                <BuildHubInlinePreview
                    buildHub={selectedProject}
                    fieldSelection={props.fieldSelection}
                    onFieldSelectionChange={props.onFieldSelectionChange}
                />
            )}
            <FormMessage variant="darker" />
        </FormItem>
    );
}
